import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";

import {
  OlmapLayer,
  StzhOlmapFeatureWithPopover,
  StzhOlmapInitializedEvent,
  StzhOlmapMoveEndEvent,
  StzhOlmapMovePanZoomEvent,
  StzhOlmapMoveStartEvent,
  StzhOlmapPostRenderEvent,
} from "../../index";

import { StzhOlmapLocalizedText } from "./stzh-olmap.localization";

// map dependencies
import Proj4 from "proj4";
import OlGeolocation from "ol/geolocation.js";
import OlMap from "ol/map.js";
import OlOverlay from "ol/overlay.js";
import OlExtent from "ol/extent.js";
import OlView from "ol/view.js";
import OlFeature from "ol/feature";
import OlSphere from "ol/sphere";
import OlProjProjection from "ol/proj/projection";

import OlSourceWmts from "ol/source/wmts.js";
import OlSourceVector from "ol/source/vector.js";

import OlGeomPoint from "ol/geom/point.js";
import OlGeomLineString from "ol/geom/linestring.js";

import OlLayerTile from "ol/layer/tile.js";
import OlLayerVector from "ol/layer/vector.js";

import OlControl from "ol/control";
import OlControlScaleLine from "ol/control/scaleline";
import OlControlControl from "ol/control/control";

import OlTilegridWmts from "ol/tilegrid/wmts";
import OlCollection from "ol/collection";

import OlStyleStyle from "ol/style/style";
import OlStyleIcon from "ol/style/icon";

import OlInteractionDragRotate from "ol/interaction/dragrotate";
import OlInteractionDoubleClickZoom from "ol/interaction/doubleclickzoom";
import OlInteractionDragPan from "ol/interaction/dragpan";
import OlInteractionPinchRotate from "ol/interaction/pinchrotate";
import OlInteractionPinchZoom from "ol/interaction/pinchzoom";
import OlInteractionKeyboardPan from "ol/interaction/keyboardpan";
import OlInteractionKeyboardZoom from "ol/interaction/keyboardzoom";
import OlInteractionDragZoom from "ol/interaction/dragzoom";

import Compass from "./assets/compass.svg";

window.proj4 = Proj4;

window.proj4.defs(
  "EPSG:2056",
  "+proj=somerc +lat_0=46.95240555555556 +lon_0=7.439583333333333 +k_0=1 +x_0=2600000 +y_0=1200000 +ellps=bessel +towgs84=674.374,15.056,405.346,0,0,0,0 +units=m +no_defs",
);

const STYLES_MARKER_ICON = {
  anchor: [0.5, 1],
  anchorXUnits: "fraction",
  anchorYUnits: "fraction",
  scale: 0.5,
  src: `${window.stzhComponents.pathMedia}/olmap/marker.png`,
};

const STYLES_ACTIVE_MARKER_ICON = {
  ...STYLES_MARKER_ICON,
  src: `${window.stzhComponents.pathMedia}/olmap/marker-active.png`,
};

const STYLES_POSITION_MARKER_ICON_STYLES = {
  ...STYLES_MARKER_ICON,
  anchor: [0.5, 0.5],
  src: `${window.stzhComponents.pathMedia}/olmap/marker-position.png`,
};

/**
 *
 */
@Component({
  tag: "stzh-olmap",
  styleUrl: "stzh-olmap.scss",
})
export class StzhOlmap {
  /** Translation strings */
  @Prop() localization: StzhOlmapLocalizedText;

  /** Hostname */
  @Prop() hostname: string = "www.ogc.stadt-zuerich.ch";

  /** Whether map scale should be shown */
  @Prop() scaleControl: boolean = true;

  /** Whether map layer switch control is shown */
  @Prop() layerControl: boolean = true;

  /** Whether map should be printable */
  @Prop() printControl: boolean = true;

  /** Whether map should have a location control */
  @Prop() locationControl: boolean = false;

  /** Active layer (`streets` or `satellite` can be used with default `layers` prop value) */
  @Prop({ mutable: true }) layer: string = "streets";

  /** Initial zoom */
  @Prop() zoom: number = 7;

  /** Zoom set when location is enabled */
  @Prop() zoomLocation: number = 7;

  /** Initial center position in the format of Swiss Grid LV95 coordinates */
  @Prop()
  center: string | [number, number] = [2683311, 1247082];
  private _center: [number, number];

  /** Whether to display an icon marker in the center */
  @Prop() centerMarker: boolean = true;

  /** Minimal angle in radians to start a rotation */
  @Prop() pinchRotateThreshold: number = 0.3;

  /** Resolutions */
  @Prop() resolutions: string | number[] = [
    67.73333333333, 33.8666666667, 16.9333333333, 8.4666666667, 4.2333333333, 2.1166666667, 1.0583333333, 0.5291666667,
    0.2645833333, 0.1322916667, 0.0661458333,
  ];

  /** Minimal angle in radians to start a rotation */
  @Prop({ mutable: true }) writeExtentToUrlEnabled: boolean = false;

  private _resolutions: number[];

  /**
   * Layers (maximum of two).
   *
   * Default layers `streets` and `satellite` are defined.
   * If `streets` or `satellite` is used as key, they will have a default filled `title`.
   */
  @Prop() layers: string | { [key: string]: OlmapLayer } = {
    streets: {
      thumbnailUrl: `${window.stzhComponents.pathMedia}/olmap/thumb-streets.jpg`,
      options: {
        url: "https://{Hostname}/mapproxy/wmts/1.0.0/{Layer}/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png",
        layer: "basiskarte_zuerich_gebaeudeschraegansicht",
        matrixSet: "ktzh",
        origin: [2480237.0, 1315832.0],
        extent: [2666867.0, 1220962.0, 2719695.5, 1285537.0],
      },
    },
    satellite: {
      thumbnailUrl: `${window.stzhComponents.pathMedia}/olmap/thumb-satellite.jpg`,
      options: {
        url: "https://{Hostname}/mapproxy/wmts/1.0.0/{Layer}/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png",
        layer: "orthofoto",
        matrixSet: "ktzh",
        origin: [2480237.0, 1315832.0],
        extent: [2666867.0, 1220962.0, 2719695.5, 1285537.0],
      },
    },
  };

  private _layers: { [key: string]: OlmapLayer } = {};

  @Element() element: HTMLStzhOlmapElement;

  @State() _initialExtent: any;

  @Watch("layers")
  layersWatcher(newValue: string | { [key: string]: OlmapLayer }) {
    if (typeof newValue === "string") {
      this._layers = JSON.parse(newValue);
    } else {
      this._layers = newValue;
    }
  }

  @Watch("resolutions")
  resolutionsWatcher(newValue: string | number[]) {
    if (typeof newValue === "string") {
      this._resolutions = JSON.parse(newValue);
    } else {
      this._resolutions = newValue;
    }
  }

  @Watch("zoom")
  zoomWatcher(newValue) {
    if (this.map) {
      this.map.getView().setZoom(newValue);
    }
  }

  @Watch("scaleControl")
  scaleControlWatcher(newValue: boolean) {
    if (!this.map) {
      return;
    }

    if (newValue) {
      this.scaleControlOl = new OlControlScaleLine({
        units: "metric",
      });

      this.map.addControl(this.scaleControlOl);
    } else {
      if (this.scaleControl) {
        this.map.removeControl(this.scaleControlOl);
      }

      this.scaleControlOl = null;
    }
  }

  @Watch("printControl")
  printControlWatcher(newValue: boolean) {
    if (!this.map) {
      return;
    }

    if (newValue) {
      this.printControlElement = document.createElement("button");
      this.printControlElement.className = "ol-print";
      this.printControlElement.type = "button";
      this.printControlElement.ariaLabel = this.localization.printLabel;
      this.printControlElement.addEventListener("click", this.handlePrintButtonClick);
      this.printControlElement.innerHTML = '<stzh-icon name="print" class="ol-print__icon"></stzh-icon>';

      this.printControlOl = new OlControlControl({
        element: this.printControlElement,
      });

      this.map.addControl(this.printControlOl);
    } else {
      if (this.printControlOl) {
        this.map.removeControl(this.printControlOl);
      }

      this.printControlOl = null;
      this.printControlElement = null;
    }
  }

  @Watch("layerControl")
  layerControlWatcher(newValue: boolean) {
    if (!this.map || this.layersArray.length === 1) {
      return;
    }

    if (newValue) {
      this.layerControlElement = document.createElement("button");
      this.layerControlElement.className = "ol-layer";
      this.layerControlElement.type = "button";
      this.layerControlElement.addEventListener("click", () => {
        this.layer = this.layer === "streets" ? "satellite" : "streets";
      });

      this.layerControlImageElement = document.createElement("img");
      this.layerControlImageElement.className = "ol-layer__thumbnail";
      this.layerControlImageElement.setAttribute("role", "presentation");
      this.layerControlImageElement.setAttribute("alt", "");

      this.layerControlTextElement = document.createElement("span");
      this.layerControlTextElement.className = "ol-layer__text";

      this.layerControlElement.appendChild(this.layerControlImageElement);
      this.layerControlElement.appendChild(this.layerControlTextElement);

      this.layerControlOl = new OlControlControl({
        element: this.layerControlElement,
      });

      this.map.addControl(this.layerControlOl);
    } else {
      if (this.layerControlOl) {
        this.map.removeControl(this.layerControlOl);
      }

      this.layerControlOl = null;
      this.layerControlElement = null;
      this.layerControlImageElement = null;
      this.layerControlTextElement = null;
    }
  }

  @Watch("locationControl")
  locationControlWatcher(newValue: boolean) {
    if (!this.map) {
      return;
    }

    if (newValue) {
      this.locationControlElement = document.createElement("button");
      this.locationControlElement.type = "button";
      this.locationControlElement.className = "ol-location";
      this.locationControlElement.innerHTML = '<stzh-icon name="target-bullet" class="ol-location__icon"></stzh-icon>';

      this.locationControlElement.addEventListener("click", () => {
        const isTracking = this.geolocation.getTracking();

        if (isTracking) {
          this.disableLocationTracking().catch();
        } else {
          this.enableLocationTracking().catch();
        }
      });

      this.locationControlOl = new OlControlControl({
        element: this.locationControlElement,
      });

      this.geolocation = new OlGeolocation({
        // take the projection to use from the map's view
        projection: this.map.getView().getProjection(),
      });

      this.positionMarkerOl = new OlFeature();
      this.positionMarkerOl.setId("position");

      const iconStyle = new OlStyleStyle({
        image: new OlStyleIcon(STYLES_POSITION_MARKER_ICON_STYLES),
      });

      this.positionMarkerOl.setStyle(iconStyle);
      this.positionLayer.getSource().addFeature(this.positionMarkerOl);

      // listen to changes in position
      this.geolocation.on("change:position", () => {
        const position = this.geolocation.getPosition();

        // zuerich location for testing (bprm6280, Ausbildungszentrum Rohwiesen)
        // const position = [2685067, 1252482];

        this.positionMarkerOl.setGeometry(new OlGeomPoint(position));

        // if position is within map and first time the position is changed, we center to it
        if (
          !this.geoPositionChanged &&
          OlExtent.containsExtent(
            this._layers[this.layer].options.extent,
            this.positionMarkerOl.getGeometry().getExtent(),
          )
        ) {
          this.geoPositionChanged = true;
          this.map.getView().setCenter(position);

          if (this.zoomLocation) {
            this.map.getView().setZoom(this.zoomLocation);
          }
        }
      });

      this.geolocation.on("change:tracking", () => {
        const isTracking = this.geolocation.getTracking();

        // reset position
        this.positionMarkerOl.setGeometry(null);
        this.setLocationControlState(isTracking);
      });

      this.geolocation.on("error", () => {
        this.setLocationControlState(false);
      });

      this.map.addControl(this.locationControlOl);
    } else {
      this.disableLocationTracking().catch();

      if (this.locationControlOl) {
        this.map.removeControl(this.locationControlOl);
      }

      if (this.positionMarkerOl) {
        this.positionLayer.getSource().removeFeature(this.positionMarkerOl);
      }

      this.locationControlOl = null;
      this.locationControlElement = null;
      this.geolocation = null;
      this.positionMarkerOl = null;
    }
  }

  @Watch("layer")
  layerWatcher(newValue: string) {
    if (!this.map) {
      return;
    }

    Object.entries(this.rasterLayers).forEach(([key, layer]) => {
      if (key === newValue) {
        layer.setVisible(true);

        this.map.getView().set("projection", this.projections[newValue]);

        const inactiveLayer = this.layersArray.find(([key]) => key !== this.layer);

        if (inactiveLayer) {
          if (this.layerControlImageElement) {
            this.layerControlImageElement.src = inactiveLayer[1].thumbnailUrl;
          }

          if (this.layerControlTextElement) {
            let defaultTitle = "";

            if (key === "streets") {
              defaultTitle = this.localization.streetsLayerTitle;
            } else if (key === "satellite") {
              defaultTitle = this.localization.satelliteLayerTitle;
            }

            this.layerControlTextElement.textContent = inactiveLayer[1].title || defaultTitle;
          }
        }
      } else {
        layer.setVisible(false);
      }
    });
  }

  @Watch("center")
  centerWatcher(newValue: string | [number, number]) {
    if (typeof newValue === "string") {
      this._center = JSON.parse(newValue);
    } else {
      this._center = newValue;
    }

    if (this.map && !this._initialExtent) {
      this.map.getView().setCenter(this._center);

      if (this.centerMarkerOl) {
        this.centerMarkerOl.setGeometry(new OlGeomPoint(this._center));
      }
    }
  }

  @Watch("centerMarker")
  async centerMarkerWatcher(newValue: boolean) {
    if (newValue) {
      // add marker in the center position
      this.centerMarkerOl = await this.addIconMarker(this._center);
    } else if (this.centerMarkerOl) {
      this.removeIconMarker(this.centerMarkerOl);
    }
  }

  /** Get map */
  @Method()
  async getMap(): Promise<any> {
    return this.map;
  }

  /** Write the current extend to the URL (if enabled) */
  @Method()
  async writeExtentToUrl() {
    if (this.writeExtentToUrlEnabled) {
      const view = this.map.getView();
      const extent = view.calculateExtent(this.map.getSize()); // Get the map extent

      // Extent values are in the order: [minX, minY, maxX, maxY]
      const [minX, minY, maxX, maxY] = extent;

      // Get current URL parameters
      const urlParams = new URLSearchParams(window.location.search);

      // Update the extent values in the URL
      urlParams.set("extent[0]", minX.toFixed(10));
      urlParams.set("extent[1]", minY.toFixed(10));
      urlParams.set("extent[2]", maxX.toFixed(10));
      urlParams.set("extent[3]", maxY.toFixed(10));

      // Update the URL without reloading the page
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      history.replaceState(null, "", newUrl);
    }
  }

  /** Get all visible markers */
  @Method()
  async getVisibleMarkers() {
    const source = this.iconLayer.getSource();
    const features = source.getFeatures();

    if (features.length === 0) {
      return []; // No markers
    }

    // Get the current extent of the map view
    const viewExtent = this.map.getView().calculateExtent(this.map.getSize());

    // Filter markers that are visible within the current extent
    return features.filter(feature => {
      const markerExtent = feature.getGeometry().getExtent();
      return OlExtent.intersects(viewExtent, markerExtent);
    });
  }

  /** Add a icon marker to the icon layer. */
  @Method()
  async findMarkerById(id: string) {
    const features = this.iconLayer.getSource().getFeatures();
    return features.find(feature => feature.get("id") === id);
  }

  /** Add a icon marker to the icon layer. */
  @Method()
  async addIconMarker(coordinates: number[], props?: StzhOlmapFeatureWithPopover) {
    const marker = new OlFeature({
      geometry: new OlGeomPoint(coordinates),
    });

    const iconStyle = new OlStyleStyle({
      image: new OlStyleIcon(STYLES_MARKER_ICON),
    });

    if (props && props.id) {
      marker.setProperties({ id: props.id });
      marker.setId(props.id);
    }
    marker.setProperties({ stzh: props });
    marker.setStyle(iconStyle);
    this.iconLayer.getSource().addFeature(marker);

    return marker;
  }

  /** Add a icon markers to the icon layer. */
  @Method()
  async addIconMarkers(
    iconMarkers: {
      coordinates: number[];
      props?: StzhOlmapFeatureWithPopover;
    }[],
  ) {
    for (const iconMarker of iconMarkers) {
      await this.addIconMarker(iconMarker.coordinates, iconMarker.props);
    }
    return true;
  }

  @Method()
  async updateViewToFitMarkers(animationDuration = 1000) {
    const source = this.iconLayer.getSource();
    const features = source.getFeatures();

    if (features.length === 0) {
      return; // No markers, so we don't need to update the view.
    }

    // Create an empty extent.
    let extent = OlExtent.createEmpty();

    // Loop through each feature (marker) and extend the extent to include its geometry.
    features.forEach(feature => {
      OlExtent.extend(extent, feature.getGeometry().getExtent());
    });

    // Get the map view and fit it to the extent.
    this.map.getView().fit(extent, {
      size: this.map.getSize(),
      duration: animationDuration,
    });
  }

  /** Remove all icon markers from icon layer. */
  @Method()
  async removeAllIconMarkers() {
    return this.iconLayer.getSource().clear();
  }

  /** Remove icon marker from icon layer. */
  @Method()
  async removeIconMarker(marker) {
    return this.iconLayer.getSource().removeFeature(marker);
  }

  /** Set icon marker as active. */
  @Method()
  async setIconMarkerActive(marker) {
    marker.setStyle(
      new OlStyleStyle({
        image: new OlStyleIcon(STYLES_ACTIVE_MARKER_ICON),
      }),
    );
  }

  /** Set icon marker as inactive. */
  @Method()
  async setIconMarkerInactive(marker) {
    marker.setStyle(
      new OlStyleStyle({
        image: new OlStyleIcon(STYLES_MARKER_ICON),
      }),
    );
  }

  /** Set all icon markers as inactive. */
  @Method()
  async setAllIconMarkersInactive() {
    const source = this.iconLayer.getSource();
    const features = source.getFeatures();

    // Loop through all the features and set them inactive
    features.forEach(feature => {
      this.setIconMarkerInactive(feature).catch(err => console.error(err));
    });
  }

  /** Enable user location tracking */
  @Method()
  async enableLocationTracking(centerOnFirstGeoPositionChange = true) {
    this.setLocationControlState(true);

    this.geoPositionChanged = !centerOnFirstGeoPositionChange;

    if (this.geolocation) {
      this.geolocation.setTracking(true);
    }
  }

  /** Disable user location tracking */
  @Method()
  async disableLocationTracking() {
    this.setLocationControlState(false);

    if (this.geolocation) {
      this.geolocation.setTracking(false);
    }
  }

  /** Get distance between two points in meters */
  @Method()
  async getDistance(startCoords, endCoords) {
    const line = new OlGeomLineString([startCoords, endCoords]);

    const length = OlSphere.getLength(line, {
      projection: this.map.getView().getProjection(),
    });

    return length;
  }

  /** Olmap initialized event */
  @Event() stzhInitialized: EventEmitter<StzhOlmapInitializedEvent>;

  /** Olmap postrender event */
  @Event() stzhPostRender: EventEmitter<StzhOlmapPostRenderEvent>;

  /** Olmap movestart event */
  @Event() stzhMoveStart: EventEmitter<StzhOlmapMoveStartEvent>;

  /** Olmap moveend event */
  @Event() stzhMoveEnd: EventEmitter<StzhOlmapMoveEndEvent>;

  /** Olmap move pan zoom event */
  @Event() stzhMovePanZoom: EventEmitter<StzhOlmapMovePanZoomEvent>;

  @Listen("load", { target: "window" })
  @Listen("deviceorientation", { target: "window" })
  @Listen("pageshow", { target: "window" })
  handleLoad() {
    if (this.map) {
      requestAnimationFrame(() => {
        this.map.updateSize();
      });
    }
  }

  private map: OlMap;
  private mapElement: HTMLElement;
  private popoverElement: HTMLElement;
  private geolocation: OlGeolocation;
  private printIframe: HTMLIFrameElement;

  private layersArray: [string, OlmapLayer][];
  private rasterLayers: { [key: string]: OlLayerTile };
  private projections: { [key: string]: OlProjProjection };

  private positionMarkerOl: OlFeature;
  private centerMarkerOl: OlFeature;

  private positionLayer: OlLayerVector;
  private iconLayer: OlLayerVector;

  private geoPositionChanged: boolean = false;

  // controls

  private layerControlOl: OlControlControl;
  private layerControlElement: HTMLButtonElement;
  private layerControlTextElement: HTMLElement;
  private layerControlImageElement: HTMLImageElement;

  private scaleControlOl: OlControlScaleLine;

  private printControlOl: OlControlControl;
  private printControlElement: HTMLButtonElement;

  private locationControlOl: OlControlControl;
  private locationControlElement: HTMLButtonElement;

  private handlePostRender = () => {
    this.stzhPostRender.emit({
      component: "stzh-olmap",
    });
  };

  private handleDoubleClick = () => {
    this.stzhMovePanZoom.emit({
      component: "stzh-olmap",
    });
  };

  private handleMoveStart = () => {
    this.stzhMoveStart.emit({
      component: "stzh-olmap",
    });
  };

  private handleMoveEnd = () => {
    this.stzhMoveEnd.emit({
      component: "stzh-olmap",
    });
    this.writeExtentToUrl().catch();
  };

  private handlePointerdrag = () => {
    this.stzhMovePanZoom.emit({
      component: "stzh-olmap",
    });
  };

  private handleChangeResolution = () => {
    this.stzhMovePanZoom.emit({
      component: "stzh-olmap",
    });
  };

  private handleInsideModalOpen = () => {
    if (this.map) {
      requestAnimationFrame(() => {
        this.map.updateSize();
      });
    }
  };

  private showPopover(coordinate: [number, number], properties: StzhOlmapFeatureWithPopover) {
    if (properties.imageUri) {
      this.popoverElement.innerHTML = `<div class="stzh-olmap__popover-image"><img src="${properties.imageUri}" alt="${properties.title}"></div>`;
    }
    let contentDiv = '<div class="stzh-olmap__popover-content">';
    if (properties.title) {
      contentDiv += `<stzh-heading curve="p2">${properties.title}</stzh-heading>`;
    }
    if (properties.text) {
      contentDiv += `<stzh-text size="milli" color="info">${properties.text}</stzh-text>`;
    }
    if (properties.data && properties.data.length) {
      properties.data.map(dataItem => {
        contentDiv += `<div class="stzh-poi-teaser-list-item">
                          <stzh-icon name="${dataItem.icon}"></stzh-icon>
                          <stzh-text size="micro">
                            ${dataItem.text}
                          </stzh-text>
                        </div>`;
      });
    }
    contentDiv += "</div>";
    this.popoverElement.innerHTML += contentDiv;
    if (properties.href) {
      this.popoverElement.innerHTML += `<a href="${properties.href}" class="stzh-olmap__popover-link"></a>`;
    }

    // Create an overlay to position the popover on the map
    const overlay = new OlOverlay({
      element: this.popoverElement,
      position: coordinate,
      positioning: "bottom-center", // Adjust the positioning as needed
      offset: [0, -50], // Optional offset for positioning the popover
      autoPan: false,
      autoPanAnimation: {
        duration: 300,
      },
    });

    // Add the overlay to the map
    this.map.addOverlay(overlay);
    this.map.renderSync();
  }

  private initializeMarkerHover() {
    this.map.on("pointermove", event => {
      const pixel = this.map.getEventPixel(event.originalEvent);
      const hit = this.map.hasFeatureAtPixel(pixel, {
        layerFilter: layer => layer === this.iconLayer, // Ensure we only detect markers from this layer
      });

      // Change the cursor style based on whether the mouse is over a marker
      const target = this.map.getTargetElement(); // Get the map DOM element
      if (hit) {
        target.style.cursor = "pointer"; // Change to pointer when hovering over a marker
      } else {
        target.style.cursor = ""; // Reset cursor when not hovering over a marker
      }
    });
  }

  private handlePrintButtonClick = () => {
    // TODO: check with client if print is still needed and if related contact component needs to be printed too
    if (!this.printIframe) {
      this.printIframe = document.createElement("iframe");
      Object.assign(this.printIframe.style, {
        height: "0px",
        width: "0px",
        position: "absolute",
        border: "none",
      });

      this.element.append(this.printIframe);
    }

    // empty iframe
    this.printIframe.innerHTML = "";

    const printWindow = this.printIframe.contentWindow;
    const componentCss = (this.constructor as any).style.replace('@charset "UTF-8";', "");
    const globalCssLinks = Array.from(document.querySelectorAll("link")).map(node => node.outerHTML);

    let img;

    this.map.once("postcompose", function (event) {
      const canvas = event.context.canvas;
      img = canvas.toDataURL("image/png");
    });

    this.map.renderSync();

    printWindow.document.body.innerHTML = `
      ${globalCssLinks.join("")}

      <style type="text/css">
        ${componentCss}
      </style>

      <div class="stzh-olmap__map stzh-olmap__map--print">
        <img class="stzh-olmap__print-image" src="${img}">
        ${this.element.querySelector(".ol-scale-line.ol-unselectable").outerHTML}
      </div>
    `;

    setTimeout(() => {
      // acts as a bugfix for IE 11
      // https://stackoverflow.com/questions/24673024/unable-to-print-iframe-content-from-parent-page-in-ie-11
      try {
        printWindow.document.execCommand("print", false, null);
      } catch (e) {
        printWindow.print();
      }
    }, 1000);
  };

  private setLocationControlState(active) {
    if (this.positionLayer) {
      this.positionLayer.setVisible(active);
    }

    if (this.locationControlElement) {
      this.locationControlElement.classList.toggle("active", active);
    }
  }

  private createMap() {
    const zoomIn = document.createElement("stzh-icon");
    zoomIn.ariaLabel = this.localization.zoomInLabel;
    zoomIn.name = "plus";
    zoomIn.addEventListener("click", () => {
      this.stzhMovePanZoom.emit({
        component: "stzh-olmap",
      });
    });

    const zoomOut = document.createElement("stzh-icon");
    zoomOut.ariaLabel = this.localization.zoomOutLabel;
    zoomOut.name = "minus";
    zoomOut.addEventListener("click", () => {
      this.stzhMovePanZoom.emit({
        component: "stzh-olmap",
      });
    });

    const rotate = document.createElement("div");
    rotate.innerHTML = Compass;

    const rotateText = document.createElement("span");
    rotateText.innerText = this.localization.rotateLabel;

    rotate.appendChild(rotateText);

    const map = new OlMap({
      controls: OlControl.defaults({
        // https://openlayers.org/en/v4.6.5/apidoc/olx.control.html#.ZoomOptions
        zoomOptions: {
          zoomInLabel: zoomIn,
          zoomOutLabel: zoomOut,
          zoomInTipLabel: "",
          zoomOutTipLabel: "",
        },
        // https://openlayers.org/en/v4.6.5/apidoc/olx.control.html#.RotateOptions
        rotateOptions: {
          label: rotate,
          tipLabel: "",
        },
      }),
      interactions: new OlCollection([
        new OlInteractionDragRotate(),
        new OlInteractionDoubleClickZoom(),
        new OlInteractionDragPan(),
        new OlInteractionPinchRotate({
          threshold: this.pinchRotateThreshold,
        }),
        new OlInteractionPinchZoom(),
        new OlInteractionKeyboardPan(),
        new OlInteractionKeyboardZoom(),
        new OlInteractionDragZoom(),
      ]),
      loadTilesWhileAnimating: true,
      loadTilesWhileInteracting: true,
      target: this.mapElement,
      logo: false,
    });

    return map;
  }

  private createRasterLayer(key, options) {
    return new OlLayerTile({
      title: "wmts",
      type: "base",
      extent: options.extent,
      source: this.createLayerSource(key, options),
    });
  }

  private createLayerSource(key, options) {
    if (!this.projections) {
      this.projections = {};
    }

    this.projections[key] = new OlProjProjection({
      code: "EPSG:2056",
      extent: options.extent,
    });

    return this.creatRasterSource(key, options);
  }

  private creatRasterSource(key, options) {
    const { url, layer, matrixSet, origin } = options;
    const matrixIds = [];

    for (let i = 0; i < this._resolutions.length; i++) {
      matrixIds.push(i);
    }

    const tileGrid = new OlTilegridWmts({
      origin,
      resolutions: this._resolutions,
      matrixIds,
      tileSize: [512, 512],
    });

    return new OlSourceWmts({
      url,
      tileGrid: tileGrid,
      style: "default",
      projection: this.projections[key],
      crossOrigin: "anonymous",
      layer,
      matrixSet,
      requestEncoding: "REST",
    });
  }

  private createIconLayer() {
    return new OlLayerVector({
      source: new OlSourceVector({}),
    });
  }

  private initMap() {
    this.map = this.createMap();

    // events from https://openlayers.org/en/v4.6.5/apidoc/ol.Map.html
    this.map.on("postrender", this.handlePostRender);
    this.map.on("movestart", this.handleMoveStart);
    this.map.on("dblclick", this.handleDoubleClick);
    this.map.on("moveend", this.handleMoveEnd);
    this.map.on("pointerdrag", this.handlePointerdrag);
    this.map.on("change:resolution", this.handleChangeResolution);

    this.layersArray = Object.entries(this._layers);

    if (this.layersArray.length > 2) {
      throw new Error("There are only two different layers allowed.");
    }

    this.rasterLayers = {};
    this.layersArray.forEach(([key, layer]) => {
      layer.options.url = layer.options.url.replace(/\{hostname\}/gi, this.hostname);
      this.rasterLayers[key] = this.createRasterLayer(key, layer.options);
      this.map.addLayer(this.rasterLayers[key]);
    });

    // add the VectorLayer which is used for adding the position marker to the map
    this.positionLayer = this.createIconLayer();
    this.positionLayer.setVisible(false);
    this.map.addLayer(this.positionLayer);

    // add the VectorLayer which is used for adding icon markers to the map
    this.iconLayer = this.createIconLayer();
    this.map.addLayer(this.iconLayer);

    const view = new OlView({
      center: this._center,
      projection: this.projections[this.layer],
      zoom: this.zoom,
      resolutions: this._resolutions,
    });

    // Apply the extent if it exists
    if (this._initialExtent) {
      view.fit(this._initialExtent, { size: this.map.getSize() });
    }

    this.map.setView(view);

    this.centerWatcher(this.center);
    this.scaleControlWatcher(this.scaleControl);
    this.printControlWatcher(this.printControl);
    this.locationControlWatcher(this.locationControl);
    this.layerControlWatcher(this.layerControl);
    this.layerWatcher(this.layer);
    this.centerMarkerWatcher(this.centerMarker);
    this.initializeMarkerHover();

    this.map.on("singleclick", event => {
      this.setAllIconMarkersInactive().catch();
      this.popoverElement.innerHTML = "";
      this.map.forEachFeatureAtPixel(event.pixel, feature => {
        if (feature.getGeometry() instanceof OlGeomPoint) {
          this.setIconMarkerActive(feature).catch();
          const props = feature.getProperties();
          if (props.stzh) {
            this.showPopover(props.geometry.flatCoordinates, props.stzh as StzhOlmapFeatureWithPopover);
          }
          return true;
        }
      });
    });

    requestAnimationFrame(() => {
      this.stzhInitialized.emit({
        component: "stzh-olmap",
      });
    });

    this.handleMoveEnd();
  }

  componentShouldUpdate(_newValue, _oldValue, prop: string) {
    return ["hostname", "localization", "layers", "resolutions", "pinchRotateThreshold"].includes(prop);
  }

  async componentWillLoad() {
    this.layersWatcher(this.layers);
    this.resolutionsWatcher(this.resolutions);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "olmap");
    }

    // Parse URL parameters to get extent
    const urlParams = new URLSearchParams(window.location.search);
    const extent = [
      parseFloat(urlParams.get("extent[0]")),
      parseFloat(urlParams.get("extent[1]")),
      parseFloat(urlParams.get("extent[2]")),
      parseFloat(urlParams.get("extent[3]")),
    ];

    // If extent is valid, set the map's view accordingly
    if (!isNaN(extent[0]) && !isNaN(extent[1]) && !isNaN(extent[2]) && !isNaN(extent[3])) {
      this._initialExtent = extent; // Save this to apply during map initialization
    }
  }

  componentWillUpdate() {
    if (this.map) {
      this.map.setTarget(null);
      this.map = null;
    }
  }

  componentDidRender() {
    if (this.map) {
      return;
    }

    this.initMap();
  }

  connectedCallback() {
    const dialog = this.element.closest("stzh-dialog");

    if (dialog) {
      dialog.addEventListener("stzhOpen", this.handleInsideModalOpen);
    }
  }

  disconnectedCallback() {
    if (this.map) {
      this.map.setTarget(null);
      this.map = null;
    }

    const dialog = this.element.closest("stzh-dialog");

    if (dialog) {
      dialog.removeEventListener("stzhOpen", this.handleInsideModalOpen);
    }
  }

  render() {
    const classes = {
      "stzh-olmap": true,
      "stzh-olmap--has-no-layer-control": !this.layerControl,
      "stzh-olmap--has-no-print-control": !this.printControl,
      "stzh-olmap--has-location-control": this.locationControl,
    };

    return (
      <Host>
        <div class={classes}>
          <div ref={el => (this.mapElement = el as HTMLDivElement)} class="stzh-olmap__map"></div>
          <div ref={el => (this.popoverElement = el as HTMLDivElement)} class="stzh-olmap__popover"></div>
        </div>
      </Host>
    );
  }
}
